import {createGlobalState} from '@vueuse/core'
import {markRaw, shallowRef, toRaw} from 'vue'
import {DEFAULT_MASKED_REVEAL_LIMIT} from '@toolify/server/src/services/DatabasePropertyService/constants/maskedRevealLimit'
import {DatabaseItemState} from '@toolify/server/src/models/mongoose/DatabaseItemModel/enum/DatabaseItemState'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'

export interface IRevealedMaskedCell {
  databaseItemId: string
  databasePropertyId: string
  revealUntil: Date
  value: unknown
}

export interface IRevealMaskedDatabaseItemCellPayload {
  databaseItemId: string
  databasePropertyId: string
  revealUntil: Date
  value: unknown
  maxRevealedCells?: number
}

export function getMaskedDatabaseCellKey(databaseItemId: string, databasePropertyId: string) {
  return `${databaseItemId}-${databasePropertyId}`
}

export function getIsMaskedCellRevealActive(revealUntil: Date | undefined, now: number = Date.now()) {
  return !!(revealUntil && revealUntil.getTime() > now)
}

export interface IMaskedCellOwnerItem {
  state?: DatabaseItemState
  cellValues?: Record<string, {isMasked?: boolean} | undefined>
}

export function getIsMaskedCellOfPersistedItem(
  databaseItem: IMaskedCellOwnerItem | null | undefined,
  databasePropertyId: string,
  databaseProperty?: {isMasked?: boolean} | null,
) {
  if(!databaseItem || databaseItem.state === DatabaseItemState.PendingSubmission) {
    return false
  }
  return !!(databaseItem.cellValues?.[databasePropertyId]?.isMasked || databaseProperty?.isMasked)
}

export function getMaskedCellRevealErrorMessageKey(error: unknown): string {
  if(error instanceof ApiException && error.name === KnownError.TOO_MANY_REQUESTS) {
    return 'database.maskedCell.revealRateLimited'
  }
  if(error instanceof ApiException && error.name === KnownError.NOT_ALLOWED) {
    return 'database.maskedCell.revealNotAllowed'
  }
  return 'database.maskedCell.revealFailed'
}

export function toRawMaskedCellValue<T>(value: T): T {
  if(typeof value !== 'object' || value === null) {
    return value
  }
  return markRaw(toRaw(value))
}

const useState = createGlobalState(() => ({
  revealsByKey: shallowRef(new Map<string, IRevealedMaskedCell>()),
  revealUntilByKey: shallowRef(new Map<string, Date>()),
  expiryTimerByKey: new Map<string, ReturnType<typeof setTimeout>>(),
}))

function pruneExpiredReveals(reveals: Map<string, IRevealedMaskedCell>): Map<string, IRevealedMaskedCell> {
  const now = Date.now()
  const next = new Map<string, IRevealedMaskedCell>()
  for(const [key, reveal] of reveals.entries()) {
    if(getIsMaskedCellRevealActive(reveal.revealUntil, now)) {
      next.set(key, reveal)
    }
  }
  return next
}

function evictOldestRevealsOfProperty(
  reveals: Map<string, IRevealedMaskedCell>,
  databasePropertyId: string,
  maxRevealedCells: number,
): IRevealedMaskedCell[] {
  const evicted: IRevealedMaskedCell[] = []
  const keysOfProperty = Array.from(reveals.entries())
    .filter(([, reveal]) => reveal.databasePropertyId === databasePropertyId)
    .map(([key]) => key)
  while(keysOfProperty.length >= Math.max(1, maxRevealedCells)) {
    const oldestKey = keysOfProperty.shift()
    const oldestReveal = reveals.get(oldestKey)
    reveals.delete(oldestKey)
    if(oldestReveal) {
      evicted.push(oldestReveal)
    }
  }
  return evicted
}

export function useMaskedDatabaseProperties() {
  const {revealsByKey, revealUntilByKey, expiryTimerByKey} = useState()

  function expireReveal(key: string) {
    expiryTimerByKey.delete(key)
    if(!revealsByKey.value.has(key)) {
      return
    }
    const next = new Map(revealsByKey.value)
    next.delete(key)
    commit(next)
  }

  function syncExpiryTimers(reveals: Map<string, IRevealedMaskedCell>) {
    for(const [key, timerId] of Array.from(expiryTimerByKey.entries())) {
      if(reveals.get(key) !== revealsByKey.value.get(key)) {
        clearTimeout(timerId)
        expiryTimerByKey.delete(key)
      }
    }
    for(const [key, reveal] of reveals.entries()) {
      if(expiryTimerByKey.has(key)) {
        continue
      }
      const delayMs = Math.max(0, reveal.revealUntil.getTime() - Date.now())
      expiryTimerByKey.set(key, setTimeout(() => expireReveal(key), delayMs))
    }
  }

  function commit(reveals: Map<string, IRevealedMaskedCell>) {
    syncExpiryTimers(reveals)
    revealsByKey.value = reveals
    revealUntilByKey.value = new Map(
      Array.from(reveals.entries()).map(([key, reveal]) => [key, reveal.revealUntil]),
    )
  }

  function getActiveReveal(databaseItemId: string, databasePropertyId: string): IRevealedMaskedCell | undefined {
    const reveal = revealsByKey.value.get(getMaskedDatabaseCellKey(databaseItemId, databasePropertyId))
    return reveal && getIsMaskedCellRevealActive(reveal.revealUntil) ? reveal : undefined
  }

  function getIsMaskedDatabaseItemCellRevealed(databaseItemId: string, databasePropertyId: string) {
    return !!getActiveReveal(databaseItemId, databasePropertyId)
  }

  function getRevealedMaskedCellValue(databaseItemId: string, databasePropertyId: string): unknown {
    return getActiveReveal(databaseItemId, databasePropertyId)?.value
  }

  function revealMaskedDatabaseItemCell({
    databaseItemId,
    databasePropertyId,
    revealUntil,
    value,
    maxRevealedCells = DEFAULT_MASKED_REVEAL_LIMIT,
  }: IRevealMaskedDatabaseItemCellPayload): {evicted: IRevealedMaskedCell[]} {
    const key = getMaskedDatabaseCellKey(databaseItemId, databasePropertyId)
    const next = pruneExpiredReveals(revealsByKey.value)
    next.delete(key)
    const evicted = evictOldestRevealsOfProperty(next, databasePropertyId, maxRevealedCells)
    next.set(key, {
      databaseItemId,
      databasePropertyId,
      revealUntil,
      value: toRawMaskedCellValue(value),
    })
    commit(next)
    return {evicted}
  }

  function updateRevealedMaskedCellValue(databaseItemId: string, databasePropertyId: string, value: unknown) {
    const reveal = getActiveReveal(databaseItemId, databasePropertyId)
    if(!reveal) {
      return
    }
    const next = new Map(revealsByKey.value)
    next.set(getMaskedDatabaseCellKey(databaseItemId, databasePropertyId), {
      ...reveal,
      value: toRawMaskedCellValue(value),
    })
    commit(next)
  }

  function hideMaskedDatabaseItemCell(databaseItemId: string, databasePropertyId: string) {
    const next = new Map(revealsByKey.value)
    next.delete(getMaskedDatabaseCellKey(databaseItemId, databasePropertyId))
    commit(next)
  }

  function clearAllReveals() {
    commit(new Map())
  }

  return {
    maskedCellRevealsByKey: revealsByKey,
    maskedCellRevealUntilByKey: revealUntilByKey,
    getIsMaskedDatabaseItemCellRevealed,
    getRevealedMaskedCellValue,
    revealMaskedDatabaseItemCell,
    updateRevealedMaskedCellValue,
    hideMaskedDatabaseItemCell,
    clearAllReveals,
  }
}
